/**
 * Centralizes the server-side pagination + optional status-filter query lifecycle
 * shared by every ADSP tree level (CCG, concept, creative, asset). Keeping the
 * page state, filter-driven reset, and page-count clamp in one hook stops each
 * level from re-deriving the same lazy-loading behavior the Google EntityGroupRow
 * uses. Only the CONCEPT level is status-filtered; the other levels omit the
 * filter so a CCG and everything under a visible concept always render.
 * The Meta/TikTok catalog view reuses it for its product set and product levels by
 * passing `publisher` and `parentPublisherEntityId`; ADSP omits both.
 */
import { useEffect, useState } from 'react';
import type { Publisher, PublisherEntityType } from 'src/store/generated/leela-api-generated';
import { usePublisherEntitiesControllerListForCampaignQuery } from 'src/store/leela-api';
import { ADS_STATUS_FILTER_PARAMS, type AdsStatusFilter } from './statusFilterParams';

export const ADSP_LEVEL_PAGE_SIZE = 25;

/** Largest page the publisher-entities endpoint serves (and its parent-id list cap). */
export const ADSP_MAX_PAGE_SIZE = 100;

interface AdspEntityLevelArgs {
  campaignId: string;
  /** Publisher to query; defaults to ADSP. The Meta/TikTok catalog view reuses this hook. */
  publisher?: Publisher;
  publisherEntityType: PublisherEntityType;
  /** Status bucket to apply server-side; omitted to list every row regardless of status. */
  statusFilter?: AdsStatusFilter;
  /**
   * Restricts the query to one parent by its source id; omitted for the root CCG level.
   * Only pass URL-safe values (e.g. ACP ids): request signing mis-canonicalizes reserved
   * characters, so free-text ids here get the request rejected before it reaches the service.
   */
  parentSourceEntityId?: string;
  /**
   * Restricts the query to children of any of these parent rows, by row `_id`. Hex ids are
   * always URL-safe, unlike source ids that embed free-text titles.
   */
  parentEntityIds?: string[];
  /**
   * Restricts the query to children of this publisher-side parent id (e.g. a Meta/TikTok
   * product set id, which is numeric and URL-safe); omitted for ADSP.
   */
  parentPublisherEntityId?: string;
  /** Rows per page; defaults to ADSP_LEVEL_PAGE_SIZE. */
  pageSize?: number;
  /** Server sort tokens (e.g. ['createdAt:-1']); omitted to accept the default. */
  sort?: string[];
  /** Defers the request until the level is expanded, matching lazy loading. */
  skip?: boolean;
}

type LevelQuery = ReturnType<typeof usePublisherEntitiesControllerListForCampaignQuery>;

interface AdspEntityLevel {
  page: number;
  query: LevelQuery;
  setPage: (page: number) => void;
}

/**
 * A stable identity for "which result set is this": when it changes, page N of the
 * old set is meaningless for the new one. Joined with a separator ids cannot contain.
 */
function buildResultSetKey ({
  parentEntityIds,
  parentPublisherEntityId,
  parentSourceEntityId,
  sort,
  statusFilter,
}: Pick<AdspEntityLevelArgs, 'parentEntityIds' | 'parentPublisherEntityId' | 'parentSourceEntityId' | 'sort' | 'statusFilter'>): string {
  return [
    statusFilter ?? '',
    parentSourceEntityId ?? '',
    parentPublisherEntityId ?? '',
    (sort ?? []).join(','),
    ...(parentEntityIds ?? []),
  ].join('\u0000');
}

export function useAdspEntityLevel ({
  campaignId,
  pageSize = ADSP_LEVEL_PAGE_SIZE,
  parentEntityIds,
  parentPublisherEntityId,
  parentSourceEntityId,
  publisher = 'adsp',
  publisherEntityType,
  skip = false,
  sort,
  statusFilter,
}: AdspEntityLevelArgs): AdspEntityLevel {
  const [page, setPage] = useState(1);
  const resultSetKey = buildResultSetKey({ parentEntityIds, parentPublisherEntityId, parentSourceEntityId, sort, statusFilter });

  // A new status bucket, parent, or sort restarts paging so the first page always reflects it.
  useEffect(() => {
    setPage(1);
  }, [resultSetKey]);

  const query = usePublisherEntitiesControllerListForCampaignQuery({
    campaignId,
    page,
    pageSize,
    parentSourceEntityId,
    ...(parentEntityIds != null && parentEntityIds.length > 0 && { parentEntityIds }),
    ...(parentPublisherEntityId != null && { parentPublisherEntityId }),
    publisher,
    publisherEntityType,
    sort,
    ...(statusFilter != null && ADS_STATUS_FILTER_PARAMS[statusFilter]),
  }, { skip });

  // Guard against a stale page after the result set shrinks (e.g. refetch).
  const pageCount = query.data?.meta.pagination.pageCount;
  useEffect(() => {
    if (pageCount !== undefined && pageCount < page) {
      setPage(Math.max(1, pageCount));
    }
  }, [page, pageCount]);

  return { page, query, setPage };
}
